import { Modal } from 'src/components/Modal';
import { SUPER_PROJECT_NAME, SVG_SIZE_L, SVG_SIZE_M } from 'src/constants';
import { Infinity, ListBullets, X } from '@phosphor-icons/react';
import {
  AppRoutes,
  CanonicalInterval,
  WelcomeUpsellModalTypeV2,
} from 'src/types';
import styles from './WelcomeTierModal.module.scss';
import { useNavigate } from 'react-router-dom';
import { env } from 'src/env';
import { WELCOME_MODALS_DATA_V2 } from 'src/constants/welcomeTierModal';
import welcomeNinjaIcon from 'src/images/icons/welcomeNinjaIcon.svg';
import { Button } from 'src/v2/commonComponents/Button';
import { useEntitlements, useGetUser } from 'src/hooks';
import { useMemo } from 'react';

interface WelcomeUpsellModalProps {
  modalType: WelcomeUpsellModalTypeV2;
  isOpen: boolean;
  onClose: () => void;
}

export const WelcomeTierModal = ({
  modalType,
  isOpen,
  onClose,
}: WelcomeUpsellModalProps) => {
  const navigate = useNavigate();
  const { tier_id } = useGetUser();

  const isFree = modalType === 'free-tier-welcome-modal';

  const welcomeUpsellData = WELCOME_MODALS_DATA_V2[modalType];

  const isBusiness = modalType.includes('business');

  const { entitlements } = useEntitlements({
    isBusiness,
    interval: CanonicalInterval.MONTHLY,
  });

  const requestsCount = useMemo(() => {
    return (
      entitlements.find((item) => item.tier === tier_id)?.limits?.total
        ?.value || 0
    )
      .toString()
      .replace('-1', 'Unlimited');
  }, [entitlements, tier_id]);

  if (!welcomeUpsellData) {
    return null;
  }

  const { title, description, features, isMax, upgradeButtonId } =
    welcomeUpsellData;

  const handleTrySuperAgentButtonClick = () => {
    onClose();
    window.location.href = env.REACT_APP_SUPER_NINJA_URL;
  };

  const handleStartFirstTaskButtonClick = async () => {
    onClose();
  };

  const handleUpgradeClick = async () => {
    navigate(AppRoutes.PAYMENT);
    onClose();
  };

  const startFirstTaskButton = (
    <Button
      color="highlight"
      onClick={
        isMax ? handleTrySuperAgentButtonClick : handleStartFirstTaskButtonClick
      }
    >
      Start first task
    </Button>
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      showCloseIcon={false}
      blockScroll={false}
    >
      <div className={styles.root} data-e2e={modalType}>
        <button type="button" className={styles.buttonClose} onClick={onClose}>
          <X size={SVG_SIZE_L} />
        </button>

        <div className={styles.main}>
          <div className={styles.iconWrapper}>
            <img
              src={welcomeNinjaIcon}
              width={224}
              height={224}
              alt="Ninja logo"
            />
          </div>
          <div className={styles.titleWrapper}>
            <h2 className={styles.title}>{title}</h2>
            {description && <h4 className={styles.subtitle}>{description}</h4>}
          </div>
          <div className={styles.featuresListWrapper}>
            <div className={styles.featureItemRow}>
              <ListBullets size={SVG_SIZE_L} />
              <div className={styles.textSection}>
                <h6
                  className={styles.featureTitle}
                >{`Unlimited Ninja tasks + ${requestsCount} ${SUPER_PROJECT_NAME} tasks per month`}</h6>
                <div className={styles.featureDescription}>
                  Transform your ideas into finished deliverables
                </div>
              </div>
            </div>
            {features.map((item) => (
              <div key={item.title} className={styles.featureItemRow}>
                {item.icon}
                <div className={styles.textSection}>
                  <h6 className={styles.featureTitle}>{item.title}</h6>
                  <div className={styles.featureDescription}>
                    {item.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.footer}>
            {!isFree ? (
              <div className={styles.singleButtonWrapper}>
                {!isMax && (
                  <Button
                    color="secondary-outline"
                    onClick={handleTrySuperAgentButtonClick}
                  >
                    Try {SUPER_PROJECT_NAME}
                  </Button>
                )}
                {startFirstTaskButton}
              </div>
            ) : (
              <div className={styles.buttonsRowWrapper}>
                <Button
                  color="link"
                  className={styles.upgradeButton}
                  id={upgradeButtonId}
                  onClick={handleUpgradeClick}
                >
                  <Infinity size={SVG_SIZE_M} />
                  Unlock unlimited tasks
                </Button>
                <div className={styles.rightSideButtonsWrapper}>
                  <Button
                    color="secondary-outline"
                    onClick={handleTrySuperAgentButtonClick}
                  >
                    Try {SUPER_PROJECT_NAME}
                  </Button>
                  {startFirstTaskButton}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
